import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomValidators } from '@shared/customValidators';
import { collectFormErrors } from '@shared/formHelpers';
import { BusinessActivitySurvey, SalesChannel } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'sales-channel',
  templateUrl: './sales-channel.component.html',
  styleUrl: './sales-channel.component.scss'
})
export class SalesChannelComponent implements OnInit {
    private readonly subStepName = "Company Sales Channel";
    private businessActivitySurveyToSave: BusinessActivitySurvey;
    loading = false;
    form: UntypedFormGroup;
    otherChannelMaxlength: number;

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const businessActivitySurvey: BusinessActivitySurvey = this.route.snapshot.data.businessActivitySurvey;
        this.businessActivitySurveyToSave = {
            ...businessActivitySurvey
        };
        this.otherChannelMaxlength = CustomValidators.getBusinessActivitySurveyMaxlength("salesChannelDescription")
        this.form = this.fb.group({
            "retailChannel": [businessActivitySurvey?.salesChannel?.includes(SalesChannel.RETAIL)],
            "onlineChannel": [businessActivitySurvey?.salesChannel?.includes(SalesChannel.ONLINE)],
            "otherChannelSelected": [businessActivitySurvey?.salesChannel?.includes(SalesChannel.OTHER)]
        });

        if (businessActivitySurvey?.salesChannel?.includes(SalesChannel.OTHER)) {
            this.showFreeTextOption(businessActivitySurvey?.salesChannelDescription);
        }

        this.form.get("otherChannelSelected").valueChanges.subscribe((value) => {
            if (value) {
                this.showFreeTextOption();
                this.amplitudeService.trackFreeTextSurveyOptionSelected(this.subStepName);
            } else if (this.form.get("otherChannel")) {
                this.hideFreeTextOption();
            }
        });
    }

    private showFreeTextOption(value: string = null) {
        if (!this.form.get("otherChannel")) {
            this.form.addControl("otherChannel", new UntypedFormControl(value, [
                Validators.required,
                Validators.maxLength(this.otherChannelMaxlength)
            ]));
        }
    }

    private hideFreeTextOption() {
        this.form.removeControl("otherChannel");
    }

    private isValid(): boolean {
        this.form.markAllAsTouched();

        const options = [
            this.form.value['retailChannel'],
            this.form.value['onlineChannel'],
            this.form.value['otherChannelSelected']
        ];

        if (options.every(v => v !== true)) {
            this.form.setErrors({
                selectOneOrMore: true
            });
            return false;
        }

        return this.form.valid;
    }

    previousStep(): void {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    async nextStep() {
        if (this.isValid()) {
            this.loading = true;
            const formValues = this.form.value;

            const selection: string[] = [];
            if (formValues.retailChannel) {
                selection.push(SalesChannel.RETAIL);
            }
            if (formValues.onlineChannel) {
                selection.push(SalesChannel.ONLINE);
            }
            if (formValues.otherChannelSelected) {
                selection.push(SalesChannel.OTHER);
            }

            this.businessActivitySurveyToSave.salesChannel = selection.join(";") ;
            this.businessActivitySurveyToSave.salesChannelDescription = formValues.otherChannelSelected ? formValues.otherChannel : null;
            if (this.form.controls['otherChannel']) {
                this.amplitudeService.trackFreeTextSurveyOptionSubmitted(this.subStepName);
            }

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            try {
                await this.registrationService.saveBusinessActivitySurvey(this.businessActivitySurveyToSave);
                this.trackSubStepCompleted();
                this.registrationService.completeStep(REGISTRATION_STEP_ID.SALES_CHANNEL);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            } catch (e) {
                this.logger.error('Save business activity survey failed', e, 'with business activity survey', this.businessActivitySurveyToSave);
                this.loading = false;
                this.dynatrace.leaveAction(submitActionId);
            }
        } else {
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
        }
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(this.subStepName);
        this.gtmService.trackSubStepViewed(this.subStepName);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(this.subStepName);
        this.gtmService.trackSubStepCompleted(this.subStepName);
    }


}
